import { toast } from "sonner";
import {
  useManufacturerDeliveryFees,
  useUpdateManufacturerDeliveryFee,
} from "@/api/delivery-fees";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { formatMoney } from "@/lib/utils";

/** What the manufacturer charges for each delivery extra its free freight excludes. */
export function DeliveryFees({
  manufacturerUuid,
}: {
  manufacturerUuid: string;
}) {
  const fees = useManufacturerDeliveryFees(manufacturerUuid);
  const update = useUpdateManufacturerDeliveryFee(manufacturerUuid);

  return (
    <section aria-labelledby="delivery-extras" className="mt-4">
      <h4 id="delivery-extras" className="mb-2 text-sm font-semibold">
        Delivery extras
      </h4>
      {fees.isError ? (
        <p className="rounded-lg border px-3 py-2.5 text-sm text-destructive">
          Couldn't load delivery extras.
        </p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {(fees.data ?? []).map((fee) => {
            const recorded = fee.amount != null;
            return (
              <li
                key={fee.accessorial}
                className="flex min-h-11 items-center justify-between gap-3 px-3 py-1 text-sm"
              >
                <span className="min-w-0">{fee.label}</span>
                <POInlineEditableField
                  kind="money"
                  align="right"
                  value={fee.amount}
                  displayValue={
                    recorded
                      ? formatMoney(fee.amount)
                      : `Default ${formatMoney(fee.fallback_amount)}`
                  }
                  buttonClassName={
                    recorded ? undefined : "text-muted-foreground"
                  }
                  ariaLabel={`${fee.label} fee`}
                  title={fee.notes ?? undefined}
                  showEditIcon
                  pending={
                    update.isPending &&
                    update.variables?.accessorial === fee.accessorial
                  }
                  onConfirm={(raw) => {
                    const trimmed = raw.trim();
                    const amount = trimmed === "" ? null : Number(trimmed);
                    if (
                      amount !== null &&
                      (!Number.isFinite(amount) || amount < 0)
                    ) {
                      toast.error("Enter a fee of $0 or more");
                      return;
                    }
                    update.mutate(
                      {
                        accessorial: fee.accessorial,
                        amount: amount === null ? null : amount.toFixed(2),
                      },
                      {
                        onError: () =>
                          toast.error(
                            `Couldn't save the ${fee.label.toLowerCase()} fee`,
                          ),
                      },
                    );
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
